// Icon system for page blocks.
//
// This module used to map ~120 lucide names to EMOJI with a `✨` fallback.
// Emoji are the single most "cheap template" visual tell in published
// output: they render in a different style on every OS, ignore the theme's
// colors, can't take a stroke weight, and sit on their own baseline. Every
// `icon?: string` field in the schema (FeatureItem, ButtonSpec, callouts,
// timeline markers, nav items, bento cells, steps…) went through it.
//
// It now renders REAL inline SVG from `lucide-react`, which is:
//   • already a dependency on all four surfaces (the shared renderers import
//     from it, and the static SSR prerender resolves it fine — lucide icons
//     are plain SVG components with no browser APIs),
//   • tree-shaken, because the curated set below uses explicit named imports
//     rather than a namespace import,
//   • inlined at render time, so the static site needs no icon font, no
//     sprite sheet and no extra request, and
//   • themeable — `currentColor` strokes pick up the surrounding text color,
//     and size/strokeWidth are props.
//
// `resolveIcon` (the emoji resolver) is retained and still used for the few
// places that need a plain STRING rather than a node, but authored icons now
// route through `<PageIcon>`.

import React from 'react'
import {
  Activity, AlertCircle, AlertTriangle, ArrowDown, ArrowLeft, ArrowRight, ArrowUp,
  Award, BarChart3, Bell, Book, BookOpen, Bookmark, Box, Briefcase, Building2,
  Calendar, CalendarDays, Camera, Check, CheckCircle2, CheckSquare, ChevronDown,
  ChevronLeft, ChevronRight, ChevronUp, Circle, Clock, Cloud, Code2, Cpu,
  CreditCard, Database, DollarSign, Download, Droplet, Eye, EyeOff, Facebook,
  Feather, FileText, Filter, Flag, Flame, Folder, Gift, Github, GitBranch,
  GitMerge, GitPullRequest, Globe, GraduationCap, Grid3x3, Heart, HelpCircle,
  Home, Image as ImageIcon, Inbox, Info, Instagram, Key, Layers, Leaf, Lightbulb,
  Linkedin, List, Loader2, Lock, Mail, Map as MapIcon, MapPin, Medal, Menu,
  MessageCircle, MessageSquare, Minus, MinusCircle, Monitor, Moon, Music,
  Package, Paperclip, PauseCircle, Pencil, Phone, PieChart, PlayCircle, Plus,
  PlusCircle, Rocket, Rss, Search, Send, Server, Settings, Share2, Shield,
  ShieldCheck, ShoppingBag, ShoppingCart, SkipBack, SkipForward, SlidersHorizontal,
  Smartphone, Sparkles, Star, StopCircle, Sun, Tag, Target, Terminal, ThumbsUp,
  Timer, TrendingDown, TrendingUp, Trash2, Trees, Trophy, Truck, Twitter, Unlock,
  Upload, User, UserPlus, Users, Video, Wallet, Wrench, X, XCircle, Youtube, Zap,
  type LucideIcon,
} from 'lucide-react'

/**
 * The curated icon set. Keys are the canonical (lucide, kebab-case) names the
 * AI generator and the property panels emit; aliases below widen what
 * resolves without growing the bundle.
 */
const ICONS: Record<string, LucideIcon> = {
  activity: Activity,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  award: Award,
  'bar-chart': BarChart3,
  bell: Bell,
  book: Book,
  'book-open': BookOpen,
  bookmark: Bookmark,
  box: Box,
  briefcase: Briefcase,
  building: Building2,
  calendar: Calendar,
  'calendar-days': CalendarDays,
  camera: Camera,
  check: Check,
  'check-circle': CheckCircle2,
  'check-square': CheckSquare,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  circle: Circle,
  clock: Clock,
  cloud: Cloud,
  code: Code2,
  cpu: Cpu,
  'credit-card': CreditCard,
  database: Database,
  'dollar-sign': DollarSign,
  download: Download,
  droplet: Droplet,
  eye: Eye,
  'eye-off': EyeOff,
  facebook: Facebook,
  feather: Feather,
  'file-text': FileText,
  filter: Filter,
  flag: Flag,
  fire: Flame,
  folder: Folder,
  gift: Gift,
  github: Github,
  'git-branch': GitBranch,
  'git-merge': GitMerge,
  'git-pull-request': GitPullRequest,
  globe: Globe,
  'graduation-cap': GraduationCap,
  grid: Grid3x3,
  heart: Heart,
  'help-circle': HelpCircle,
  home: Home,
  image: ImageIcon,
  inbox: Inbox,
  info: Info,
  instagram: Instagram,
  key: Key,
  layers: Layers,
  leaf: Leaf,
  lightbulb: Lightbulb,
  linkedin: Linkedin,
  list: List,
  loader: Loader2,
  lock: Lock,
  mail: Mail,
  map: MapIcon,
  'map-pin': MapPin,
  medal: Medal,
  menu: Menu,
  'message-circle': MessageCircle,
  'message-square': MessageSquare,
  minus: Minus,
  'minus-circle': MinusCircle,
  monitor: Monitor,
  moon: Moon,
  music: Music,
  package: Package,
  paperclip: Paperclip,
  pause: PauseCircle,
  pencil: Pencil,
  phone: Phone,
  'pie-chart': PieChart,
  play: PlayCircle,
  plus: Plus,
  'plus-circle': PlusCircle,
  rocket: Rocket,
  rss: Rss,
  search: Search,
  send: Send,
  server: Server,
  settings: Settings,
  share: Share2,
  shield: Shield,
  'shield-check': ShieldCheck,
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  'skip-back': SkipBack,
  'skip-forward': SkipForward,
  sliders: SlidersHorizontal,
  smartphone: Smartphone,
  sparkles: Sparkles,
  star: Star,
  stop: StopCircle,
  sun: Sun,
  tag: Tag,
  target: Target,
  terminal: Terminal,
  'thumbs-up': ThumbsUp,
  timer: Timer,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  trash: Trash2,
  tree: Trees,
  trophy: Trophy,
  truck: Truck,
  twitter: Twitter,
  unlock: Unlock,
  upload: Upload,
  user: User,
  'user-plus': UserPlus,
  users: Users,
  video: Video,
  wallet: Wallet,
  wrench: Wrench,
  x: X,
  'x-circle': XCircle,
  youtube: Youtube,
  zap: Zap,
}

/**
 * Synonyms → canonical key. These are the names the AI generator and older
 * saved pages actually emit, so they must keep resolving even though they
 * are not lucide's own spelling.
 */
const ALIASES: Record<string, string> = {
  bolt: 'zap',
  folders: 'folder',
  tags: 'tag',
  sparkle: 'sparkles',
  idea: 'lightbulb',
  envelope: 'mail',
  'phone-call': 'phone',
  'video-camera': 'video',
  'code-2': 'code',
  'trash-2': 'trash',
  edit: 'pencil',
  crosshair: 'target',
  droplets: 'droplet',
  pin: 'map-pin',
  tool: 'wrench',
  'x-square': 'x-circle',
  'info-circle': 'info',
  'alert-octagon': 'alert-triangle',
  'shield-alert': 'shield',
  'thumbs-down': 'thumbs-up',
  euro: 'dollar-sign',
  'chart-bar': 'bar-chart',
  chart: 'bar-chart',
  document: 'file-text',
  file: 'file-text',
  team: 'users',
  customer: 'user',
  support: 'help-circle',
  security: 'shield',
  speed: 'zap',
  growth: 'trending-up',
  x_twitter: 'twitter',
}

/** Every icon name the picker offers, sorted for a stable UI. */
export const ICON_NAMES: string[] = Object.keys(ICONS).sort()

/** Normalise an authored value to a canonical icon key (or null). */
export function iconKey(raw: string | undefined | null): string | null {
  if (!raw) return null
  const s = String(raw).trim().toLowerCase()
  if (!s) return null
  const k = s.replace(/^lucide[-:]/, '').replace(/[_\s]+/g, '-')
  if (ICONS[k]) return k
  const alias = ALIASES[k]
  if (alias && ICONS[alias]) return alias
  return null
}

/** True when the authored value is (or aliases to) a real vector icon. */
export function isVectorIcon(raw: string | undefined | null): boolean {
  return iconKey(raw) !== null
}

export interface PageIconProps {
  /** Authored icon value: a lucide name, an alias, or a literal emoji. */
  name?: string | null
  size?: number
  /** Stroke weight. 1.5 reads better at small sizes than lucide's default 2. */
  strokeWidth?: number
  /** Defaults to `currentColor`, so an icon inherits its context's color. */
  color?: string
  className?: string
  style?: React.CSSProperties
  /** Rendered when the name resolves to nothing. Default: no output. */
  fallback?: React.ReactNode
}

/**
 * Render an authored icon.
 *
 * Resolution order, and why:
 *   1. A literal emoji or single glyph the author typed is passed through —
 *      an author who deliberately wrote "🎉" should get "🎉".
 *   2. A known lucide name (or alias) renders as inline SVG.
 *   3. Anything else renders `fallback` (nothing by default) rather than the
 *      old `✨`, which quietly turned every typo into a sparkle.
 */
export function PageIcon({
  name, size = 24, strokeWidth = 1.75, color, className, style, fallback = null,
}: PageIconProps) {
  if (!name) return <>{fallback}</>
  const raw = String(name).trim()
  if (!raw) return <>{fallback}</>

  // An emoji / non-ASCII glyph the author typed on purpose.
  if (NON_ASCII.test(raw)) {
    return (
      <span className={className} style={{ fontSize: size, lineHeight: 1, ...style }} aria-hidden>
        {raw}
      </span>
    )
  }

  const key = iconKey(raw)
  if (key) {
    const Cmp = ICONS[key]
    return (
      <Cmp
        size={size}
        strokeWidth={strokeWidth}
        color={color || 'currentColor'}
        className={className}
        style={style}
        aria-hidden
      />
    )
  }

  // A short literal the author typed as an icon ("→", "1", "%").
  if (raw.length <= 2) {
    return (
      <span className={className} style={{ fontSize: size, lineHeight: 1, ...style }} aria-hidden>
        {raw}
      </span>
    )
  }
  return <>{fallback}</>
}

const NON_ASCII = /[^\x20-\x7e]/

// ─── Legacy string resolver ───────────────────────────────────────────
// Retained for the handful of call sites that need a STRING (a title
// attribute, a plain-text export). Prefer `<PageIcon>` anywhere a node will
// do — it is the reason published pages stopped looking like a template.

const EMOJI: Record<string, string> = {
  bolt: '⚡', zap: '⚡', layers: '🗂', folder: '📁', flag: '🚩', tag: '🏷',
  check: '✓', 'check-circle': '✅', circle: '⚪', x: '✕',
  calendar: '📅', clock: '🕒', star: '★', heart: '❤', 'thumbs-up': '👍',
  'trending-up': '📈', 'bar-chart': '📊', 'pie-chart': '🥧',
  rocket: '🚀', sparkles: '✨', user: '👤', users: '👥',
  mail: '✉', send: '📤', inbox: '📥', phone: '📞', 'message-circle': '💬',
  home: '🏠', building: '🏢', globe: '🌐', map: '🗺', 'map-pin': '📍',
  briefcase: '💼', package: '📦', gift: '🎁', 'shopping-cart': '🛒',
  'credit-card': '💳', wallet: '👛', 'dollar-sign': '$',
  wrench: '🔧', settings: '⚙', code: '<>', terminal: '▶_',
  database: '🗄', server: '🖥', cloud: '☁', shield: '🛡', lock: '🔒',
  key: '🔑', eye: '👁', bell: '🔔', 'alert-triangle': '⚠', info: 'ℹ',
  lightbulb: '💡', 'arrow-right': '→', 'arrow-left': '←',
  play: '▶', pause: '⏸', search: '🔍', list: '☰', grid: '▦', menu: '≡',
  pencil: '✎', trash: '🗑', plus: '+', minus: '−',
  download: '⬇', upload: '⬆', paperclip: '📎', image: '🖼', camera: '📷',
  video: '🎥', music: '🎵', book: '📖', bookmark: '🔖',
  sun: '☀', moon: '🌙', leaf: '🍃', tree: '🌳', fire: '🔥', droplet: '💧',
  award: '🏆', trophy: '🏆', medal: '🎖', target: '🎯',
}

export function resolveIcon(raw: string | undefined | null): string {
  if (!raw) return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (NON_ASCII.test(s)) return s
  if (s.length === 1) return s
  const key = iconKey(s) || s.toLowerCase().replace(/^lucide[-:]/, '').replace(/_/g, '-')
  return EMOJI[key] ?? ''
}

// ── Material-Icons name normalisation ────────────────────────────────────
// Some call sites (the callout renderer, the auth-menu links) render Material
// Icons ligatures rather than SVG. Kept so those keep resolving; new code
// should use `<PageIcon>`.

const MATERIAL_ALIASES: Record<string, string> = {
  'alert-triangle': 'warning',
  'alert-circle': 'error_outline',
  'alert-octagon': 'report',
  'check-circle': 'check_circle',
  'x-circle': 'cancel',
  'help-circle': 'help',
  'info-circle': 'info',
  'minus-circle': 'remove_circle',
  'plus-circle': 'add_circle',
  lightbulb: 'lightbulb',
  star: 'star',
  heart: 'favorite',
  flag: 'flag',
  bell: 'notifications',
  shield: 'shield',
  'shield-alert': 'gpp_maybe',
  'shield-check': 'verified_user',
  lock: 'lock',
  unlock: 'lock_open',
  eye: 'visibility',
  'eye-off': 'visibility_off',
  'thumbs-up': 'thumb_up',
  'thumbs-down': 'thumb_down',
  'message-circle': 'chat_bubble',
  rocket: 'rocket_launch',
  zap: 'bolt',
  sparkles: 'auto_awesome',
}

export function normalizeIconName(value: string): string {
  if (!value) return value
  const v = value.trim()
  if (MATERIAL_ALIASES[v]) return MATERIAL_ALIASES[v]
  if (/^[a-z0-9_-]{2,}$/i.test(v)) return v.replace(/-/g, '_')
  return v
}
